import { useEffect, useMemo, useState } from 'react';
import { useProfileContext } from './useProfileContext';
import { useConfig } from './useConfig';
import { apiGetMusicians, apiGetCrew } from '../api';
import { computeRackUsage, type RackUsage } from '../lib/rackUsage';

/**
 * Rack usage across the WHOLE band: every musician + crew track (inputs) and
 * every monitor output + shared sidefill (outputs). Used by the instrument
 * and monitoring editors to grey out channels that are already patched.
 *
 * Reloads when the active band changes (and whenever the config's sidefills
 * change). The musician/crew lists are re-fetched on band switch only — the
 * editors pass their own in-memory lists through `extra`, so edits made on the
 * current page are reflected immediately.
 */
export function useRackUsage(extra?: { musicians?: unknown[]; crew?: unknown[] }): { usage: RackUsage | undefined } {
  const { activeProfileId } = useProfileContext();
  const { config } = useConfig();
  const [lists, setLists] = useState<{ musicians: unknown[]; crew: unknown[] }>({
    musicians: [],
    crew: [],
  });

  useEffect(() => {
    if (!activeProfileId) {
      setLists({ musicians: [], crew: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [musicians, crew] = await Promise.all([
          apiGetMusicians(activeProfileId),
          apiGetCrew(activeProfileId),
        ]);
        if (!cancelled) setLists({ musicians: musicians || [], crew: crew || [] });
      } catch (err) {
        console.warn('[useRackUsage] failed to load musicians/crew:', err);
        if (!cancelled) setLists({ musicians: [], crew: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeProfileId]);

  const sidefills = useMemo(() => {
    for (const mode of ['van', 'plane', 'tourbus', 'train'] as const) {
      const list = config.specs?.[mode]?.sidefills;
      if (list && list.length > 0) return list;
    }
    return [];
  }, [config.specs]);

  // In-memory lists from the editing page take precedence over the server
  // snapshot so the current edit is reflected immediately (greyed channels).
  const extraMusicians = extra?.musicians;
  const extraCrew = extra?.crew;

  const usage = useMemo<RackUsage | undefined>(() => {
    if (!activeProfileId) return undefined;
    try {
      return computeRackUsage({
        musicians: (extraMusicians ?? lists.musicians) as never,
        crew: (extraCrew ?? lists.crew) as never,
        sidefills: sidefills as never,
      });
    } catch {
      return undefined;
    }
  }, [activeProfileId, lists, extraMusicians, extraCrew, sidefills]);

  return { usage };
}
